import { Effect, Schema as S } from "effect";

/** `GET /notes` row (server `noteRoutes.ts`): the body is cut to a plain snippet. */
export const NoteSummary = S.Struct({
	id: S.String,
	title: S.String,
	tags: S.Array(S.String),
	/** "Projects/Atlas"; absent or "" is the root (older servers send none). */
	folder: S.optionalKey(S.String),
	lastUpdated: S.String,
	snippet: S.String,
});
export type NoteSummary = typeof NoteSummary.Type;

export const NoteList = S.Struct({ notes: S.Array(NoteSummary) });
export type NoteList = typeof NoteList.Type;

/** A BM25 hit. `snippet` marks matches with `**`; it is text, never HTML. */
export const SearchHit = S.Struct({
	id: S.String,
	type: S.String,
	title: S.String,
	snippet: S.String,
});
export type SearchHit = typeof SearchHit.Type;

/** `GET /notes/search`: `records` only arrives with `includeRecords=true`. */
export const NoteSearch = S.Struct({
	notes: S.Array(SearchHit),
	records: S.Array(SearchHit).pipe(
		S.withDecodingDefaultKey(Effect.succeed([] as ReadonlyArray<SearchHit>)),
	),
});
export type NoteSearch = typeof NoteSearch.Type;

/** `POST /notes` answers `{note, links}`; the list only needs the new id. */
export const CreatedNote = S.Struct({ note: S.Struct({ id: S.String }) });
export type CreatedNote = typeof CreatedNote.Type;

const LinkBase = {
	raw: S.String,
	target: S.String,
	alias: S.optionalKey(S.String),
	heading: S.optionalKey(S.String),
	block: S.optionalKey(S.String),
};

/** One `[[wiki link]]` in a note body, resolved by the server against current titles (server `shell/notes.ts` LinkView). */
export const LinkView = S.Union([
	S.Struct({
		...LinkBase,
		status: S.Literal("resolved"),
		id: S.String,
		type: S.String,
		name: S.String,
	}),
	S.Struct({
		...LinkBase,
		status: S.Literal("ambiguous"),
		ids: S.Array(S.String),
	}),
	S.Struct({ ...LinkBase, status: S.Literal("broken") }),
]);
export type LinkView = typeof LinkView.Type;

/** A note that links here, with the text around the link. */
export const Backlink = S.Struct({
	id: S.String,
	title: S.String,
	/** The linking note's folder; absent at the root. */
	folder: S.optionalKey(S.String),
	snippet: S.optionalKey(S.String),
});
export type Backlink = typeof Backlink.Type;

/** `GET /notes/backlinks?target=`; the echoed `target` is not needed. */
export const BacklinkList = S.Struct({ backlinks: S.Array(Backlink) });
export type BacklinkList = typeof BacklinkList.Type;

/** `version` is the note's `lastUpdated`; a PATCH sends it back as `baseVersion`. */
export const Note = S.Struct({
	id: S.String,
	title: S.String,
	tags: S.Array(S.String),
	folder: S.optionalKey(S.String),
	version: S.NullOr(S.String),
	body: S.String,
});
export type Note = typeof Note.Type;

/** `GET /notes/:id`. */
export const NoteDetail = S.Struct({
	note: Note,
	links: S.Array(LinkView),
	backlinks: S.Array(Backlink),
});
export type NoteDetail = typeof NoteDetail.Type;

/** `PATCH /notes/:id` answers the stored note and its re-resolved links. */
export const SavedNote = S.Struct({ note: Note, links: S.Array(LinkView) });
export type SavedNote = typeof SavedNote.Type;

/** A link whose resolution a move changed (server `core/a2a/noteFolders.ts`); only the note matters here. */
export const ChangedLink = S.Struct({
	noteId: S.String,
	title: S.String,
	raw: S.String,
});
export type ChangedLink = typeof ChangedLink.Type;

/** `GET /notes/folders`: notes directly in each folder (`count`), root notes apart. */
export const FolderCounts = S.Struct({
	rootCount: S.Number,
	folders: S.Array(S.Struct({ path: S.String, count: S.Number })),
});
export type FolderCounts = typeof FolderCounts.Type;

/** `POST /notes/:id/move` and `POST /notes/folders/rename`. */
export const FolderMove = S.Struct({ changedLinks: S.Array(ChangedLink) });
export type FolderMove = typeof FolderMove.Type;
